import {
  all,
  call,
  put,
  take,
  takeEvery,
  takeLatest,
  cancelled,
} from 'redux-saga/effects'
import { Record } from 'immutable'
import * as Diff from 'immutablediff'

import { handleError } from 'api/api-utils'
import budgetApi from 'api/BudgetApi'

import { eventChannel, END } from 'redux-saga'

import {
  createBudgetError,
  createBudgetSuccess,
  deleteBudgetError,
  deleteBudgetSuccess,
  getBudgetsError,
  getBudgetsSuccess,
  getBudgetFoldersSuccess,
  getBudgetFoldersError,
  updateBudgetError,
  updateBudgetSuccess,
  createBudgetCopyError,
  createBudgetCopySuccess,
  createBudgetFolderSuccess,
  createBudgetFolderError,
  setDefaultBudgetError,
  setDefaultBudgetSuccess,
  deleteBudgetFolderSuccess,
  deleteBudgetFolderError,
  editBudgetFolderError,
  editBudgetFolderSuccess,
  setAllBudgetRoles,
  createBudgetCopyProgress,
} from './actions'
import {
  CREATE_BUDGET,
  CREATE_BUDGET_COPY,
  DELETE_BUDGET_FOLDER,
  DELETE_BUDGET,
  GET_BUDGETS,
  SET_DEFAULT_BUDGET,
  UPDATE_BUDGET,
  GET_BUDGET_FOLDERS,
  CREATE_BUDGET_FOLDER,
  EDIT_BUDGET_FOLDER,
} from './constants'
import { differenceBy } from 'lodash'
import { tryParseErrorJson } from 'utils/parseError'

const UpdateBudgetRecord = Record({
  budgetType: null,
  endDate: null,
  lockDate: null,
  name: undefined,
  startDate: null,
  isDefault: null,
  useAccountingLockDate: null,
  realBalanceEndDate: null,
  presentationType: undefined,
  displayDecimalsAmount: null,
  timeRangeStart: null,
  timeRangeEnd: null,
  removeDimensionsFromBalanceSheet: null,
  dimensionIdWhitelist: null,
  budgetFolderIds: null,
  isPublic: null,
})

// Individual exports for testing
export function* createBudget(action) {
  const {
    accountSchemeId,
    budgetType,
    companyCode,
    endDate,
    displayDecimalsAmount,
    lockDate,
    name,
    startDate,
    budgetFolderIds,
    useAccountingLockDate,
    presentationType,
    realBalanceEndDate,
    timeRangeEnd,
    timeRangeStart,
    generateAssets,
    generateProfitAndLoss,
    generateProfitAndLossUsingMonths,
    removeDimensionsFromBalanceSheet,
    dimensionIdWhitelist,
    isPublic,
    readAndEditPermissionRoles,
    readPermissionRoles,
  } = action
  try {
    const budget = yield call(budgetApi.createBudget, {
      accountSchemeId,
      budgetType,
      companyCode,
      displayDecimalsAmount,
      endDate,
      lockDate,
      name,
      budgetFolderIds,
      startDate,
      useAccountingLockDate,
      presentationType,
      realBalanceEndDate,
      timeRangeEnd,
      timeRangeStart,
      generateAssets,
      generateProfitAndLoss,
      generateProfitAndLossUsingMonths,
      removeDimensionsFromBalanceSheet,
      dimensionIdWhitelist,
      isPublic,
      readAndEditPermissionRoles: readAndEditPermissionRoles.map(
        (role) => role.id
      ),
      readPermissionRoles: readPermissionRoles.map((role) => role.id),
    })
    yield put(createBudgetSuccess({ budget, companyCode }))
  } catch (error) {
    yield put(handleError(error, createBudgetError))
  }
}

export function* createBudgetFolder(action) {
  const { companyCode, name } = action
  try {
    const budgetFolder = yield call(budgetApi.createBudgetFolder, {
      companyCode,
      name,
    })
    yield put(createBudgetFolderSuccess({ budgetFolder, companyCode }))
  } catch (error) {
    yield put(handleError(error, createBudgetFolderError))
  }
}

export function* createBudgetCopy(action) {
  const {
    budgetId,
    companyCode,
    name,
    copyUserRights,
    copySubBudgets,
    copyData,
    copyNotes,
    copySubBudgetNotes,
    copySubBudgetRollingRules,
    copySubBudgetTags,
    copySubBudgetData,
    moveForMonths,
    dimensionIdWhitelist,
    readAndEditPermissionRoles,
    readPermissionRoles,
  } = action

  try {
    const abortController = new AbortController()
    const channel = eventChannel((eventEmit) => {
      const onMessage = (event) => {
        try {
          const response = JSON.parse(event.data)
          const budget = response.data
          if (copyUserRights) {
            budget.readAndEditPermissionRoles = readAndEditPermissionRoles
            budget.readPermissionRoles = readPermissionRoles
          }
          eventEmit(createBudgetCopySuccess({ budget, companyCode }))
          eventEmit(END)
        } catch (error) {
          eventEmit(handleError(error, createBudgetCopyError))
          eventEmit(END)
        }
      }

      const onProgress = (event) => {
        eventEmit(createBudgetCopyProgress({ progress: event.data }))
      }

      const onError = (event) => {
        const error =
          event instanceof Error
            ? event
            : { data: tryParseErrorJson(event?.data) }
        eventEmit(handleError(error, createBudgetCopyError))
        eventEmit(END)
      }

      budgetApi.copyBudget({
        budgetId,
        companyCode,
        name,
        copyUserRights,
        copySubBudgets,
        copyData,
        copyNotes,
        copySubBudgetNotes,
        copySubBudgetRollingRules,
        copySubBudgetTags,
        copySubBudgetData,
        moveForMonths,
        dimensionIdWhitelist,
        onMessage,
        onProgress,
        onError,
        abortController,
      })

      return () => {
        abortController.abort()
      }
    })
    try {
      while (true) {
        const event = yield take(channel)
        if (event === END) break
        yield put(event)
      }
    } finally {
      if (yield cancelled()) {
        channel.close()
        abortController.abort()
      }
    }
  } catch (error) {
    yield put(handleError(error, createBudgetCopyError))
  }
}

export function* deleteBudget(action) {
  const { companyCode, id } = action
  try {
    yield call(budgetApi.deleteBudget, { companyCode, id })
    yield put(deleteBudgetSuccess({ companyCode, id }))
  } catch (error) {
    yield put(handleError(error, deleteBudgetError))
  }
}

export function* editBudgetFolder(action) {
  const { companyCode, id, name } = action
  try {
    const folder = yield call(budgetApi.editBudgetFolder, {
      companyCode,
      id,
      name,
    })
    yield put(editBudgetFolderSuccess({ folder, companyCode }))
  } catch (error) {
    yield put(handleError(error, editBudgetFolderError))
  }
}

export function* deleteBudgetFolder(action) {
  const { companyCode, id } = action
  try {
    yield call(budgetApi.deleteBudgetFolder, { companyCode, id })
    yield put(deleteBudgetFolderSuccess({ companyCode, id }))
  } catch (error) {
    yield put(handleError(error, deleteBudgetFolderError))
  }
}

export function* updateBudget(action) {
  const { budget, companyCode, updatedValues } = action
  const patch = Diff(
    new UpdateBudgetRecord(budget),
    new UpdateBudgetRecord(updatedValues)
  ).toJS()
  try {
    const { readAndEditPermissionRoles, readPermissionRoles } = updatedValues

    const {
      readAndEditPermissionRoles: sourceEditPermissionRoles,
      readPermissionRoles: sourceReadPermissionRoles,
    } = budget

    const addedEditRoles = differenceBy(
      readAndEditPermissionRoles,
      sourceEditPermissionRoles,
      'id'
    )
    const removedEditRoles = differenceBy(
      sourceEditPermissionRoles,
      readAndEditPermissionRoles,
      'id'
    )

    const addedReadRoles = differenceBy(
      readPermissionRoles,
      sourceReadPermissionRoles,
      'id'
    )
    const removedReadRoles = differenceBy(
      sourceReadPermissionRoles,
      readPermissionRoles,
      'id'
    )

    const permissionClaimsUpdate = {
      readAndEditPermissionToAdd: addedEditRoles
        .map((role) => (role.hasPermissionToAllBudgets ? null : role.id))
        .filter(Boolean),
      readAndEditPermissionToRemove: removedEditRoles
        .map((role) => (role.hasPermissionToAllBudgets ? null : role.id))
        .filter(Boolean),
      readPermissionToAdd: addedReadRoles
        .map((role) => (role.hasPermissionToAllBudgets ? null : role.id))
        .filter(Boolean),
      readPermissionToRemove: removedReadRoles
        .map((role) => (role.hasPermissionToAllBudgets ? null : role.id))
        .filter(Boolean),
    }

    if (
      permissionClaimsUpdate.readAndEditPermissionToAdd.length > 0 ||
      permissionClaimsUpdate.readAndEditPermissionToRemove.length > 0 ||
      permissionClaimsUpdate.readPermissionToAdd.length > 0 ||
      permissionClaimsUpdate.readPermissionToRemove.length > 0
    ) {
      yield call(budgetApi.updateRoles, {
        companyCode,
        permissionClaimsUpdate,
        budgetId: budget.id,
      })
    }
    const updatedBudget = yield call(budgetApi.patchBudget, {
      budgetId: budget.id,
      companyCode,
      patch,
    })
    yield put(updateBudgetSuccess({ budget: updatedBudget, companyCode }))
  } catch (error) {
    yield put(handleError(error, updateBudgetError))
  }
}

export function* getBudgets(action) {
  const { companyCode } = action
  try {
    const response = yield call(budgetApi.getBudgets, companyCode)
    const {
      relationships: { readAndEditRoles, readRoles },
      data,
    } = response

    yield put(getBudgetsSuccess({ budgets: data, companyCode }))
    yield put(setAllBudgetRoles(readRoles, readAndEditRoles))
  } catch (error) {
    yield put(handleError(error, getBudgetsError))
  }
}

export function* getBudgetFolders(action) {
  const { companyCode } = action
  try {
    const folders = yield call(budgetApi.getBudgetFolders, companyCode)
    yield put(getBudgetFoldersSuccess({ folders, companyCode }))
  } catch (error) {
    yield put(handleError(error, getBudgetFoldersError))
  }
}

export function* setDefaultBudget({ companyCode, id }) {
  try {
    yield call(budgetApi.setDefault, { companyCode, id })
    yield put(setDefaultBudgetSuccess({ companyCode, id }))
  } catch (err) {
    yield put(handleError(err, setDefaultBudgetError))
  }
}

export function* budgetsSaga() {
  yield all([
    takeLatest(CREATE_BUDGET, createBudget),
    takeLatest(CREATE_BUDGET_FOLDER, createBudgetFolder),
    takeEvery(DELETE_BUDGET_FOLDER, deleteBudgetFolder),
    takeEvery(DELETE_BUDGET, deleteBudget),
    takeEvery(EDIT_BUDGET_FOLDER, editBudgetFolder),
    takeEvery(GET_BUDGETS, getBudgets),
    takeEvery(GET_BUDGET_FOLDERS, getBudgetFolders),
    takeEvery(UPDATE_BUDGET, updateBudget),
    takeLatest(CREATE_BUDGET_COPY, createBudgetCopy),
    takeEvery(SET_DEFAULT_BUDGET, setDefaultBudget),
  ])
}

export default budgetsSaga
